import { useCallback, useEffect, useRef } from 'react';
import Signature, { SignatureRef, StrokeOptions } from '@uiw/react-signature';
import ClearIcon from '~/components/icons/ClearIcon';
import TStrings from '../QuestionComponents.i18n';
import { PanelItemProps } from './SignaturePanel.types';
import { useDebounceCallback } from '~/lib/useDebounce';
import {
    appendViewBoxMetadataToElement,
    removeClassesFromSVG,
    signatureSvgElementToSvgString,
} from './Signature.utils';

const SignatureOptions: StrokeOptions = {
    size: 6,
    smoothing: 0.46,
    thinning: 0.73,
    streamline: 0.5,
    simulatePressure: true,
};

type SignatureDrawProps = PanelItemProps;
const SignatureStaticStyle = {
    backgroundColor: 'transparent',
};
const DEBOUNCE_DRAW_CONFIG = {
    options: { trailing: true, leading: false },
    timeout: 1000,
};

const SignatureDraw = ({
    readOnly,
    onSignatureChanged,
    className,
    hasValue,
}: SignatureDrawProps) => {
    const signatureRef = useRef<SignatureRef>(null);
    const onSignatureChangedLock = useRef<boolean>(false);
    const onPointer = useDebounceCallback(
        useCallback(() => {
            if (onSignatureChangedLock.current) {
                onSignatureChangedLock.current = false;
            } else {
                const svgElement = signatureRef.current?.svg;
                if (svgElement && svgElement?.innerHTML) {
                    const boundingBox = svgElement.getBBox(); // grab the bounding box from the element that is already on the DOM

                    const svgElementToBeStored = svgElement?.cloneNode(
                        true,
                    ) as SVGSVGElement; // clone the component (to make sure changes to it don't affect the ability of the user to draw)

                    appendViewBoxMetadataToElement(
                        // append viewport metadata from the original object
                        svgElementToBeStored,
                        false,
                        {
                            x: boundingBox.x,
                            y: boundingBox.y,
                            width: boundingBox.width,
                            height: boundingBox.height,
                        },
                    );
                    removeClassesFromSVG(svgElementToBeStored); // remove classes from the final component (only needed while drawing)
                    const svgString =
                        signatureSvgElementToSvgString(svgElementToBeStored);
                    if (svgString) {
                        onSignatureChanged(svgString);
                    }
                }
            }
        }, [onSignatureChanged]),
        DEBOUNCE_DRAW_CONFIG.timeout,
        DEBOUNCE_DRAW_CONFIG.options,
    );

    const clearMethod = useCallback(
        (event?: React.MouseEvent) => {
            event?.preventDefault();
            onSignatureChangedLock.current = true;
            onPointer.cancel(); // cancel any awaiting debounced callbacks
            onSignatureChanged(null);
            signatureRef.current?.clear();
        },
        [onPointer, onSignatureChanged],
    );

    return (
        <div className={`flex relative grow flex-col justify-end ${className}`}>
            <div
                className="absolute pointer-events-none"
                style={{ width: '100%' }}
            >
                <div className="inset-x-0 -mt-12 mx-auto w-[50%] border-b themed-border" />
            </div>
            <Signature
                className="flex grow"
                style={SignatureStaticStyle}
                readonly={readOnly}
                ref={signatureRef}
                options={SignatureOptions}
                onPointer={onPointer}
            />

            <div
                className="absolute flex justify-end pointer-events-none"
                style={{ width: '100%' }}
            >
                <button
                    disabled={!hasValue}
                    onClick={clearMethod}
                    className="flex flex-row p-3 items-center pointer-events-auto"
                >
                    <ClearIcon className="themed-text" />
                    <div className="px-2 themed-text text-sm">
                        {TStrings.SIGNATURE_TAB_CLEAR}
                    </div>
                </button>
            </div>
        </div>
    );
};
export default SignatureDraw;
